import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Company from "@/services/company/company";

export interface CompaniesParams {
  organizationId: string;
  page?: number;
  limit?: number;
  ownerId?: string | null;
  name?: string;
  website?: string;
  phoneNumber?: string;
  address?: string;
  linkedInUrl?: string;
  industry?: string;
  size?: string;
  startDate?: string;
  endDate?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useCompanies(params: Ref<CompaniesParams | null> | CompaniesParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["companies", "disabled"];
    return [
      "companies",
      p.organizationId,
      p.page || 1,
      p.limit || 10,
      p.ownerId ?? "all",
      p.name || "",
      p.website || "",
      p.phoneNumber || "",
      p.address || "",
      p.linkedInUrl || "",
      p.industry || "",
      p.size || "",
      p.startDate || "",
      p.endDate || "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: Record<string, any> = {
        organizationId: p.organizationId,
        page: p.page || 1,
        limit: p.limit || 25,
      };

      if (p.ownerId !== undefined) queryParams.ownerId = p.ownerId;
      if (p.name) queryParams.name = p.name;
      if (p.website) queryParams.website = p.website;
      if (p.phoneNumber) queryParams.phoneNumber = p.phoneNumber;
      if (p.address) queryParams.address = p.address;
      if (p.linkedInUrl) queryParams.linkedInUrl = p.linkedInUrl;
      if (p.industry) queryParams.industry = p.industry;
      if (p.size) queryParams.size = p.size;
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;

      const result = await Company.getCompanies(queryParams);

      if (result.data.status) {
        return {
          data: result.data.data,
          metaData: result.data.metaData,
        };
      }

      throw new Error(result.data.msg || "Failed to load companies");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
